import {
  Component,
  computed,
  inject,
  linkedSignal,
  output,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  injectAppShellThemeColors,
  provideAppShellThemeColors,
  TellentAppShellThemeDirective,
} from '@core/tellent-logic';
import { OrgThemeColors } from '@core/tellent-types';
import {
  ButtonComponent,
  ColorPickerDirective,
  HeadingComponent,
  WindowRef,
} from '@core/user-interface-legacy';

import { TellentNavigationLinkComponent } from '../tellent-navigation/navigation-link/navigation-link.component';

@Component({
  selector: 'tlc-app-shell-custom-theme',
  styleUrl: './tellent-app-shell-custom-theme.component.less',
  templateUrl: './tellent-app-shell-custom-theme.component.html',
  imports: [
    HeadingComponent,
    TranslatePipe,
    ButtonComponent,
    TellentNavigationLinkComponent,
    TellentAppShellThemeDirective,
    ColorPickerDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  viewProviders: [
    provideAppShellThemeColors({
      useFactory: () => {
        const win = inject(WindowRef).nativeWindow;
        const savedColors = injectAppShellThemeColors({ skipSelf: true });
        const isThemePristine = (colors: OrgThemeColors) =>
          Object.values(colors).every(v => v === null);

        return linkedSignal(
          computed(() => {
            const _saved = savedColors();

            if (!isThemePristine(_saved)) {
              return _saved;
            }

            const style = win.getComputedStyle(win.document.body);

            return {
              active: style.getPropertyValue('--color-nav-text-primary-active').trim(),
              links: style.getPropertyValue('--color-nav-text-strong-default').trim(),
              background: style.getPropertyValue('--color-nav-bg-neutral-default').trim(),
            };
          }),
        );
      },
    }),
  ],
})
export class TellentAppShellCustomThemeComponent {
  #i18n = inject(I18nService);

  public previewColors = <WritableSignal<OrgThemeColors>>injectAppShellThemeColors();
  public controls: Array<keyof OrgThemeColors> = ['background', 'active', 'links'];
  public save = output<OrgThemeColors>();
  public reset = output<void>();

  public labels: Record<keyof OrgThemeColors, string> = {
    background: this.#i18n.translate(
      'tl.team_members.general_settings.company_theme.setup_view.background',
    ),
    active: this.#i18n.translate(
      'tl.team_members.general_settings.company_theme.setup_view.active',
    ),
    links: this.#i18n.translate('tl.team_members.general_settings.company_theme.setup_view.links'),
  };

  public updatePreview(scope: keyof OrgThemeColors, hex: string) {
    this.previewColors.set({ ...this.previewColors(), [scope]: hex });
  }

  public saveTheme() {
    this.save.emit(this.previewColors());
  }

  public resetTheme() {
    this.reset.emit();
  }
}
